import { apiClient } from '../client';

/**
 * The organisation's outgoing webhooks (W7, SU-2). Admins only, browser
 * session. Contract: docs/webhooks.md; codes: docs/api/errors.md
 * « Webhooks sortants ».
 */

export const WEBHOOK_EVENT_TYPES = ['call.completed', 'call.meeting_booked', 'qualification.corrected', 'contact.updated'] as const;
export type WebhookEventType = typeof WEBHOOK_EVENT_TYPES[number];

export interface WebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  state: 'pending' | 'delivered' | 'failed' | 'cancelled';
  attempts: number;
  max_attempts: number;
  last_status_code: number | null;
  last_error: string | null;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
  replay_of: string | null;
}

export interface Webhook {
  id: string;
  url: string;
  description: string;
  event_types: WebhookEventType[];
  status: 'active' | 'paused';
  secret_prefix: string;
  previous_secret_expires_at: string | null;
  created_at: string;
  updated_at: string;
  last_delivery: WebhookDelivery | null;
  pending_deliveries: number;
}

export interface WebhookInput { url: string; event_types: WebhookEventType[]; description?: string }

export const listWebhooks = (signal?: AbortSignal) => apiClient.get<{ items: Webhook[] }>('/webhooks', { signal });
export const createWebhook = (body: WebhookInput) => apiClient.post<{ webhook: Webhook; secret: string }>('/webhooks', body);
export const updateWebhook = (id: string, body: Partial<WebhookInput>) => apiClient.patch<Webhook>(`/webhooks/${encodeURIComponent(id)}`, body);
export const deleteWebhook = (id: string) => apiClient.delete<{ ok: true }>(`/webhooks/${encodeURIComponent(id)}`);
export const pauseWebhook = (id: string) => apiClient.post<Webhook>(`/webhooks/${encodeURIComponent(id)}/pause`, {});
export const resumeWebhook = (id: string) => apiClient.post<Webhook>(`/webhooks/${encodeURIComponent(id)}/resume`, {});
export const rotateWebhookSecret = (id: string) => apiClient.post<{ webhook: Webhook; secret: string }>(`/webhooks/${encodeURIComponent(id)}/rotate-secret`, {});
export const testWebhook = (id: string) => apiClient.post<WebhookDelivery>(`/webhooks/${encodeURIComponent(id)}/test`, {});
export const listWebhookDeliveries = (id: string, signal?: AbortSignal) =>
  apiClient.get<{ items: WebhookDelivery[] }>(`/webhooks/${encodeURIComponent(id)}/deliveries`, { query: { limit: 50 }, signal });
export const replayWebhookDelivery = (deliveryId: string) => apiClient.post<WebhookDelivery>(`/webhooks/deliveries/${encodeURIComponent(deliveryId)}/replay`, {});
